// src/pages/AreaMasterPage.js
import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import Header from '../../layouts/Header';
import SideBar from '../../layouts/SideBar';
import { MdEdit, MdDelete } from 'react-icons/md';
import BASE_URL from '../../api';

const API_BASE_URL = `${BASE_URL}/api/master`;

const AreaMasterPage = () => {
  const [areas, setAreas] = useState([]);
  const [states, setStates] = useState([]);
  const [pincodes, setPincodes] = useState([]); // NEW: Pincode list
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // ==================== FETCH DATA ====================
  const fetchAreas = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/area`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch areas');
      const { data } = await res.json();
      setAreas(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/state`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch states');
      const { data } = await res.json();
      setStates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  // NEW: Fetch Pincodes
  const fetchPincodes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/pincode`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch pincodes');
      const { data } = await res.json();
      setPincodes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setPincodes([]);
    }
  };

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchStates(), fetchPincodes(), fetchAreas()]);
    };
    if (localStorage.getItem('token')) load();
  }, []);

  // ==================== CRUD ====================
  const createArea = async (data) => {
    const res = await fetch(`${API_BASE_URL}/area`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to create');
    }
    return res.json();
  };

  const updateArea = async (id, data) => {
    const res = await fetch(`${API_BASE_URL}/area/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update');
    }
    return res.json();
  };

  const deleteArea = async (id) => {
    const res = await fetch(`${API_BASE_URL}/area/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete');
    }
    return res.json();
  };

  // ==================== MODAL (Add/Edit) ====================
  const openModal = async (area = null) => {
    const isEdit = !!area;

    const { value: form } = await Swal.fire({
      title: isEdit ? 'Edit Area' : 'Add Area',
      width: '900px',
      html: `
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
          <div>
            <label class="block font-medium">Area Name *</label>
            <input id="areaname" class="swal2-input" value="${area?.areaname || ''}" required>
          </div>
          <div>
            <label class="block font-medium">Area Code</label>
            <input id="areacode" class="swal2-input" value="${area?.areacode || ''}">
          </div>
          <div>
            <label class="block font-medium">State *</label>
            <select id="stateid" class="swal2-select">
              <option value="">Select State</option>
              ${states.map(s => `<option value="${s._id}" ${s._id === (area?.stateid?._id || area?.stateid) ? 'selected' : ''}>${s.statename}</option>`).join('')}
            </select>
          </div>
          <div>
            <label class="block font-medium">Pincode *</label>
            <select id="pincode" class="swal2-select">
              <option value="">Select Pincode</option>
              ${pincodes.map(p => `<option value="${p._id}" ${p._id === (area?.pincode?._id || area?.pincode) ? 'selected' : ''}>${p.pincode} - ${p.locationname || 'N/A'}</option>`).join('')}
            </select>
          </div>
          <div>
            <label class="block font-medium">Hub</label>
            <input id="hub" class="swal2-input" value="${area?.hub || ''}">
          </div>
          <div>
            <label class="block font-medium">Status</label>
            <select id="status" class="swal2-select">
              <option value="true" ${area?.status !== false ? 'selected' : ''}>Enabled</option>
              <option value="false" ${area?.status === false ? 'selected' : ''}>Disabled</option>
            </select>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: isEdit ? 'Update' : 'Save',
      preConfirm: () => {
        const data = {
          ...(isEdit && { _id: area._id }),
          areaname: document.getElementById('areaname').value.trim(),
          areacode: document.getElementById('areacode').value.trim(),
          stateid: document.getElementById('stateid').value,
          pincode: document.getElementById('pincode').value,
          hub: document.getElementById('hub').value.trim(),
          status: document.getElementById('status').value === 'true',
        };

        if (!data.areaname || !data.stateid || !data.pincode) {
          Swal.showValidationMessage('Area Name, State, and Pincode are required');
          return false;
        }
        return data;
      },
    });

    if (form) {
      try {
        setLoading(true);
        if (isEdit) {
          await updateArea(area._id, form);
          toast.success('Area updated!');
        } else {
          await createArea(form);
          toast.success('Area added!');
        }
        fetchAreas();
      } catch (err) {
        toast.error(err.message || 'Error');
      } finally {
        setLoading(false);
      }
    }
  };

  // ==================== DELETE ====================
  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: 'Delete Area?',
      text: 'This cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33',
    });
    if (confirm.isConfirmed) {
      try {
        setLoading(true);
        await deleteArea(id);
        toast.success('Area removed.');
        fetchAreas();
      } catch (err) {
        toast.error(err.message || 'Error');
      } finally {
        setLoading(false);
      }
    }
  };

  // ==================== FILTER & PAGINATION ====================
  const filtered = areas.filter(a =>
    a.areaname?.toLowerCase().includes(filter.toLowerCase()) ||
    a.areacode?.toLowerCase().includes(filter.toLowerCase()) ||
    states.find(s => s._id === a.stateid?._id)?.statename?.toLowerCase().includes(filter.toLowerCase()) ||
    pincodes.find(p => p._id === a.pincode?._id)?.pincode?.includes(filter)
  );

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <>
      <div className="flex">
      <ToastContainer />
        <div className="flex-1 p-6 bg-gray-100 min-h-screen">
          <h2 className="text-2xl font-bold mb-6">Area Master</h2>

          <div className="bg-white rounded-xl shadow p-6">
            {/* Toolbar */}
            <div className="flex flex-wrap gap-4 mb-6 justify-between">
              <input
                type="text"
                placeholder="Search by Area, Code, State, Pincode..."
                value={filter}
                onChange={e => setFilter(e.target.value)}
                className="px-4 py-2 border rounded-md w-64"
              />
              <button
                onClick={() => openModal()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Area
              </button>
            </div>

            {/* Table */}
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full table-auto border-collapse">
                    <thead className="bg-blue-600 text-white">
                      <tr>
                        <th className="p-3 text-left">S.No</th>
                        <th className="p-3 text-left">Area Name</th>
                        <th className="p-3 text-left">Code</th>
                        <th className="p-3 text-left">State</th>
                        <th className="p-3 text-left">Pincode</th>
                        <th className="p-3 text-left">Hub</th>
                        <th className="p-3 text-left">Status</th>
                        <th className="p-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.length === 0 ? (
                        <tr><td colSpan="9" className="text-center py-8 text-gray-500">No areas found</td></tr>
                      ) : (
                        paginated.map((a, i) => {
                          const state = states.find(s => s._id === a.stateid?._id);
                          const pin = pincodes.find(p => p._id === a.pincode?._id);
                          return (
                            <tr key={a._id} className="border-b hover:bg-gray-50">
                              <td className="p-3">
                                {filter
                                  ? areas.findIndex(originalArea => originalArea._id === a._id) + 1
                                  : (currentPage - 1) * pageSize + i + 1}
                              </td>
                              <td className="p-3 font-medium">{a.areaname}</td>
                              <td className="p-3">{a.areacode || '-'}</td>
                              <td className="p-3">{state?.statename || '-'}</td>
                              <td className="p-3">{pin?.pincode || '-'}</td>
                              <td className="p-3">{a.hub || '-'}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded text-xs ${a.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                  {a.status ? 'Enabled' : 'Disabled'}
                                </span>
                              </td>
                              <td className="p-3 text-center">
                                <div className="flex justify-center gap-2">
                                  <button onClick={() => openModal(a)} className="text-blue-600 hover:text-blue-800">
                                    <MdEdit className="w-5 h-5" />
                                  </button>
                                  <button onClick={() => handleDelete(a._id)} className="text-red-600 hover:text-red-800">
                                    <MdDelete className="w-5 h-5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center mt-6">
                    <div className="text-sm text-gray-700">
                      Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filtered.length)} of {filtered.length} areas
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1">Page {currentPage} of {totalPages}</span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AreaMasterPage;